/**
 * "Find your size" size finder (see snippets/cx-find-size.liquid).
 * Estimates the customer's body measurements from height, weight and build, compares
 * them with the product's size chart and adds the recommended size to the cart when
 * it is in stock.
 */
import { CartAddEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

/** Default charts used when a product has no custom.size_chart metafield (cm, flat). */
const DEFAULT_CHARTS = {
  top: {
    XXS: { chest: 47, length: 65 },
    XS: { chest: 50, length: 67 },
    S: { chest: 53, length: 69 },
    M: { chest: 56, length: 71 },
    L: { chest: 59, length: 73 },
    XL: { chest: 62, length: 75 },
    XXL: { chest: 65, length: 77 },
  },
  bottom: {
    XXS: { waist: 34, inseam: 74 },
    XS: { waist: 36, inseam: 76 },
    S: { waist: 38.5, inseam: 78 },
    M: { waist: 41, inseam: 80 },
    L: { waist: 43.5, inseam: 81 },
    XL: { waist: 46, inseam: 82 },
    XXL: { waist: 48.5, inseam: 83 },
  },
};

/** Accepted size chart keys, so the chart can be written in English or Danish. */
const KEY_ALIASES = {
  chest: ['chest', 'bryst', 'brystvidde', 'width', 'bredde', 'pit_to_pit'],
  length: ['length', 'længde', 'laengde', 'body_length', 'total_length'],
  waist: ['waist', 'talje', 'livvidde', 'linning'],
  inseam: ['inseam', 'indvendig_benlængde', 'benlængde', 'inner_leg'],
};

/**
 * Defaults per garment type. A size chart can override them with "ease" ({ regular, baggy }:
 * extra room in cm, full circumference), "length_ratio" and "inseam_ratio" (wanted garment
 * length / inseam as a share of the customer's height).
 */
const DEFAULTS = {
  top: { ease: { regular: 10, baggy: 18 }, length_ratio: 0.4 },
  bottom: { ease: { regular: 4, baggy: 8 }, inseam_ratio: 0.42 },
};

const BUILD_ADJUST = { slim: -3, regular: 0, broad: 4 };

class CxFindSize extends HTMLElement {
  connectedCallback() {
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.variants = this.readJson('[data-cx-variants]') || [];
    this.chart = this.normalizeChart(this.readJson('[data-cx-chart]'));
    this.state = { step: 1, heightUnit: 'cm', weightUnit: 'kg', build: null, fit: null };

    // Delegated listeners, so section re-renders (morphs) of the inner markup keep working
    this.addEventListener('click', this.onClick, { signal });
    this.addEventListener('submit', this.onSubmit, { signal });
    this.addEventListener('input', this.updateNextState, { signal });
    this.addEventListener('close', this.onClose, { signal, capture: true });
    // The trigger button lives inside the product form, outside this element
    document.addEventListener('click', this.onTriggerClick, { signal });
  }

  /** @param {MouseEvent} event */
  onTriggerClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const trigger = target.closest('[data-cx-open]');
    if (!trigger || this.contains(trigger)) return;
    if (trigger.getAttribute('aria-controls') === this.dialog?.id) this.open();
  };

  get dialog() {
    return /** @type {HTMLDialogElement | null} */ (this.querySelector('[data-cx-dialog]'));
  }
  get form() {
    return /** @type {HTMLFormElement | null} */ (this.querySelector('[data-cx-form]'));
  }
  get result() {
    return /** @type {HTMLElement | null} */ (this.querySelector('[data-cx-result]'));
  }
  get errorEl() {
    return /** @type {HTMLElement | null} */ (this.querySelector('[data-cx-error]'));
  }
  get nextButton() {
    return /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-cx-next]'));
  }
  get backButton() {
    return /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-cx-back]'));
  }

  /** @param {MouseEvent} event */
  onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    if (target === this.dialog) return this.close(); // backdrop click

    const button = target.closest('button');
    if (!button || !this.contains(button)) return;

    if (button.hasAttribute('data-cx-open')) this.open();
    else if (button.hasAttribute('data-cx-close')) this.close();
    else if (button.hasAttribute('data-cx-back')) this.back();
    else if (button.hasAttribute('data-cx-unit')) this.onUnitClick(button);
    else if (button.hasAttribute('data-cx-choice')) this.onChoiceClick(button);
    else if (button.hasAttribute('data-cx-notify-toggle')) this.openNotify(button);
  };

  disconnectedCallback() {
    this.abortController?.abort();
    clearTimeout(this.closeTimer);
  }

  /** @param {string} selector */
  readJson(selector) {
    const el = this.querySelector(selector);
    if (!el) return null;
    try {
      const value = JSON.parse(el.textContent || 'null');
      return typeof value === 'string' ? JSON.parse(value) : value;
    } catch (error) {
      console.warn('cx-find-size: invalid JSON in', selector, error);
      return null;
    }
  }

  /**
   * Converts a size chart to { type, sizes: { SIZE: { chest, length, waist, inseam } } }
   * with flat (half circumference) widths.
   * @param {any} raw
   */
  normalizeChart(raw) {
    const type = raw?.type === 'bottom' || raw?.type === 'top' ? raw.type : this.dataset.defaultType || 'top';
    const source = raw?.sizes && typeof raw.sizes === 'object' ? raw.sizes : DEFAULT_CHARTS[type];
    /** @type {Record<string, Record<string, number>>} */
    const sizes = {};

    for (const [size, values] of Object.entries(source)) {
      if (!values || typeof values !== 'object') continue;
      const measurements = {};
      for (const [key, aliases] of Object.entries(KEY_ALIASES)) {
        const match = Object.keys(values).find((k) => aliases.includes(k.toLowerCase().trim()));
        const number = match ? parseFloat(String(values[match]).replace(',', '.')) : NaN;
        if (Number.isFinite(number) && number > 0) measurements[key] = number;
      }
      // Circumference given instead of a flat measurement
      if (measurements.chest > 80) measurements.chest /= 2;
      if (measurements.waist > 62) measurements.waist /= 2;
      if (Object.keys(measurements).length) sizes[size.trim().toUpperCase()] = measurements;
    }

    const defaults = DEFAULTS[type];
    const ease = {
      regular: Number(raw?.ease?.regular) || defaults.ease.regular,
      baggy: Number(raw?.ease?.baggy) || defaults.ease.baggy,
    };
    const lengthRatio = Number(raw?.length_ratio) || defaults.length_ratio || 0.4;
    const inseamRatio = Number(raw?.inseam_ratio) || defaults.inseam_ratio || 0.42;

    return { type, sizes, ease, lengthRatio, inseamRatio, custom: !!raw?.sizes };
  }

  open() {
    this.reset();
    this.dialog?.showModal();
    this.focusStep();
  }

  close() {
    if (this.dialog?.open) this.dialog.close();
  }

  /** @param {Event} event */
  onClose = (event) => {
    if (event.target !== this.dialog) return;
    clearTimeout(this.closeTimer);

    const { pendingSize, pendingOpenCart } = this;
    this.pendingSize = null;
    this.pendingOpenCart = false;

    // Done after closing because changing the variant re-renders the product section
    if (pendingSize) this.selectSize(pendingSize);
    if (pendingOpenCart) {
      const drawer = /** @type {any} */ (document.querySelector('cart-drawer-component'));
      if (drawer && typeof drawer.open === 'function' && !drawer.querySelector('dialog[open]')) drawer.open();
    }
  };

  reset() {
    clearTimeout(this.closeTimer);
    this.state.step = 1;
    this.state.build = null;
    this.state.fit = null;
    this.pendingSize = null;
    this.pendingOpenCart = false;
    for (const choice of this.querySelectorAll('[data-cx-choice]')) choice.setAttribute('aria-checked', 'false');
    if (this.form) this.form.hidden = false;
    if (this.result) this.result.hidden = true;
    this.hideNotify();
    this.renderStep();
  }

  renderStep() {
    const { step } = this.state;
    for (const item of this.querySelectorAll('[data-cx-stepper] [data-step]')) {
      const n = Number(item.getAttribute('data-step'));
      item.setAttribute('data-state', n === step ? 'active' : n < step ? 'done' : 'todo');
      if (n === step) item.setAttribute('aria-current', 'step');
      else item.removeAttribute('aria-current');
    }
    for (const panel of this.querySelectorAll('[data-panel]')) {
      panel.hidden = Number(panel.getAttribute('data-panel')) !== step;
    }
    if (this.backButton) this.backButton.hidden = step === 1;
    if (this.nextButton) this.nextButton.textContent = step === 4 ? 'Find my size' : 'Continue';
    this.showError('');
    this.updateNextState();
  }

  focusStep() {
    const panel = this.querySelector(`[data-panel="${this.state.step}"]`);
    const target = panel?.querySelector('input:not([hidden]):not([type="hidden"]), [data-cx-choice]');
    if (target instanceof HTMLElement && !target.closest('[hidden]')) target.focus();
  }

  updateNextState = () => {
    if (!this.nextButton) return;
    const { step, build, fit } = this.state;
    let ready = true;
    if (step === 3) ready = !!build;
    if (step === 4) ready = !!fit;
    this.nextButton.disabled = !ready;
  };

  /** @param {HTMLElement} button */
  onUnitClick(button) {
    const kind = button.dataset.cxUnit;
    const value = button.dataset.value;

    for (const sibling of this.querySelectorAll(`[data-cx-unit="${kind}"]`)) {
      sibling.setAttribute('aria-pressed', String(sibling === button));
    }

    if (kind === 'height') {
      this.state.heightUnit = value;
      for (const field of this.querySelectorAll('[data-unit-field]')) {
        field.hidden = field.getAttribute('data-unit-field') !== value;
      }
    } else if (kind === 'weight') {
      const input = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-cx-weight]'));
      const suffix = this.querySelector('[data-cx-weight-suffix]');
      if (input && value !== this.state.weightUnit && input.value) {
        const current = parseFloat(input.value);
        input.value = String(Math.round(value === 'lbs' ? current * 2.20462 : current / 2.20462));
      }
      if (input) input.placeholder = value === 'lbs' ? '154' : '70';
      if (suffix) suffix.textContent = value;
      this.state.weightUnit = value;
    }
    this.showError('');
    this.focusStep();
  }

  /** @param {HTMLElement} button */
  onChoiceClick(button) {
    const group = button.dataset.cxChoice;
    for (const sibling of this.querySelectorAll(`[data-cx-choice="${group}"]`)) {
      sibling.setAttribute('aria-checked', String(sibling === button));
    }
    this.state[group] = button.dataset.value;
    this.showError('');
    this.updateNextState();
  }

  back() {
    if (this.state.step > 1) {
      this.state.step -= 1;
      this.renderStep();
      this.focusStep();
    }
  }

  /** @param {SubmitEvent} event */
  onSubmit = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    if (target.hasAttribute('data-cx-notify-form')) return this.onNotifySubmit();
    if (target !== this.form) return;
    event.preventDefault();
    const { step } = this.state;

    if (step === 1 && this.getHeightCm() == null) {
      return this.showError('Please enter a height between 120 and 230 cm (3\'11" - 7\'7").');
    }
    if (step === 2 && this.getWeightKg() == null) {
      return this.showError('Please enter a weight between 30 and 200 kg (66 - 440 lbs).');
    }
    if (step === 3 && !this.state.build) return this.showError('Please choose your body size.');
    if (step === 4 && !this.state.fit) return this.showError('Please choose how you want it to fit.');

    if (step < 4) {
      this.state.step += 1;
      this.renderStep();
      this.focusStep();
      return;
    }

    this.finish();
  };

  /** @param {string} message */
  showError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message;
    this.errorEl.hidden = !message;
  }

  getHeightCm() {
    let cm;
    if (this.state.heightUnit === 'ft') {
      const ft = parseFloat(this.inputValue('[data-cx-height-ft]'));
      const inches = parseFloat(this.inputValue('[data-cx-height-in]') || '0');
      if (!Number.isFinite(ft)) return null;
      cm = (ft * 12 + (Number.isFinite(inches) ? inches : 0)) * 2.54;
    } else {
      cm = parseFloat(this.inputValue('[data-cx-height-cm]'));
    }
    return Number.isFinite(cm) && cm >= 120 && cm <= 230 ? cm : null;
  }

  getWeightKg() {
    let kg = parseFloat(this.inputValue('[data-cx-weight]'));
    if (this.state.weightUnit === 'lbs') kg /= 2.20462;
    return Number.isFinite(kg) && kg >= 30 && kg <= 200 ? kg : null;
  }

  /** @param {string} selector */
  inputValue(selector) {
    const input = this.querySelector(selector);
    return input instanceof HTMLInputElement ? input.value.replace(',', '.').trim() : '';
  }

  /**
   * Picks the size whose garment measurements are closest to what the customer needs.
   * Sleeve length is ignored on purpose because it differs between styles.
   */
  recommend() {
    const height = /** @type {number} */ (this.getHeightCm());
    const weight = /** @type {number} */ (this.getWeightKg());
    const build = BUILD_ADJUST[this.state.build] ?? 0;
    const fit = this.state.fit === 'baggy' ? 'baggy' : 'regular';
    const { type, sizes, ease, lengthRatio, inseamRatio } = this.chart;

    /** @type {Record<string, number>} */
    const target = {};
    if (type === 'bottom') {
      const waist = 0.55 * weight + 0.1 * height + 22 + build;
      target.waist = (waist + ease[fit]) / 2;
      target.inseam = inseamRatio * height;
    } else {
      const chest = 0.6 * weight + 0.15 * height + 26 + build;
      target.chest = (chest + ease[fit]) / 2;
      target.length = lengthRatio * height + (fit === 'baggy' ? 2 : 0);
    }

    // Main measurement counts most; being too tight is worse than a little too roomy.
    const weights = { chest: 1, waist: 1, length: 0.3, inseam: 0.2 };
    const tightPenalty = { chest: 1.5, waist: 1.5, length: 1.2, inseam: 1.2 };

    const sizeOrder = this.productSizes();
    let best = null;
    let bestScore = Infinity;

    for (const size of sizeOrder) {
      const garment = sizes[size.toUpperCase()];
      if (!garment) continue;
      let score = 0;
      let used = 0;
      for (const [key, wanted] of Object.entries(target)) {
        if (garment[key] == null) continue;
        const diff = garment[key] - wanted;
        score += Math.abs(diff) * weights[key] * (diff < 0 ? tightPenalty[key] : 1);
        used += 1;
      }
      if (!used) continue;
      // Ties: go up a size for a baggy fit, down for a regular fit
      if (score < bestScore || (score === bestScore && fit === 'baggy')) {
        best = size;
        bestScore = score;
      }
    }

    return best;
  }

  /** Size values of this product in the order they are listed. */
  productSizes() {
    const index = Number(this.dataset.optionPosition || 1) - 1;
    const seen = [];
    for (const variant of this.variants) {
      const value = variant.options?.[index];
      if (value && !seen.includes(value)) seen.push(value);
    }
    return seen;
  }

  /** @param {string} size */
  findVariant(size) {
    const index = Number(this.dataset.optionPosition || 1) - 1;
    const selected = this.currentOptions();
    const matches = this.variants.filter((v) => v.options?.[index] === size);
    // Keep the other options (e.g. colour) the customer has already selected
    return (
      matches.find((v) => v.options.every((value, i) => i === index || !selected[i] || selected[i] === value)) ||
      matches.find((v) => v.available) ||
      matches[0]
    );
  }

  /** Currently selected option values in the product's variant picker. */
  currentOptions() {
    const scope = this.closest('.shopify-section') || document;
    const json = scope.querySelector('variant-picker script[type="application/json"]');
    try {
      return JSON.parse(json?.textContent || '{}').options || [];
    } catch {
      return [];
    }
  }

  async finish() {
    const size = this.recommend();
    const sizeEl = this.querySelector('[data-cx-result-size]');
    const textEl = this.querySelector('[data-cx-result-text]');

    if (this.form) this.form.hidden = true;
    if (this.result) this.result.hidden = false;

    if (!size) {
      if (sizeEl) sizeEl.textContent = '–';
      if (textEl) {
        textEl.textContent = "Sorry, we couldn't find a size for you";
        textEl.setAttribute('data-state', 'unavailable');
      }
      return;
    }

    if (sizeEl) sizeEl.textContent = size;
    const variant = this.findVariant(size);
    this.pendingSize = size;

    if (!variant || !variant.available) {
      if (textEl) {
        textEl.textContent = 'Sorry, this size is unavailable';
        textEl.setAttribute('data-state', 'unavailable');
      }
      if (variant) this.showNotify(variant);
      return;
    }

    if (textEl) {
      textEl.textContent = 'Adding to your cart…';
      textEl.removeAttribute('data-state');
    }

    const added = await this.addToCart(variant.id);
    if (textEl) {
      textEl.textContent = added ? 'Added to your cart' : 'Sorry, this size is unavailable';
      if (!added) textEl.setAttribute('data-state', 'unavailable');
    }

    if (added) {
      this.pendingOpenCart = true;
      this.closeTimer = setTimeout(() => this.close(), 1800);
    }
  }

  /**
   * Shows the back-in-stock sign-up for a sold out variant. Uses the same customer tags as
   * snippets/cx-notify-me.liquid so sign-ups end up in the same list.
   * @param {{ id: number, title?: string }} variant
   */
  showNotify(variant) {
    const notify = this.querySelector('[data-cx-notify]');
    if (!notify) return;

    let label = this.dataset.productTitle || '';
    if (variant.title && variant.title !== 'Default Title') label += ' - ' + variant.title;

    const labelEl = this.querySelector('[data-cx-notify-label]');
    if (labelEl) labelEl.textContent = label;

    const tags = this.querySelector('[data-cx-notify-tags]');
    if (tags instanceof HTMLInputElement) {
      tags.value = 'restock-notify, restock-variant-' + variant.id + ', restock: ' + label.replace(/,/g, ' ').slice(0, 200);
    }

    this.notifyVariantId = String(variant.id);
    notify.hidden = false;
  }

  hideNotify() {
    const notify = this.querySelector('[data-cx-notify]');
    if (notify) notify.hidden = true;
    const panel = this.querySelector('[data-cx-notify-panel]');
    if (panel instanceof HTMLElement) panel.hidden = true;
    const toggle = this.querySelector('[data-cx-notify-toggle]');
    if (toggle instanceof HTMLElement) {
      toggle.hidden = false;
      toggle.setAttribute('aria-expanded', 'false');
    }
  }

  /** @param {HTMLElement} toggle */
  openNotify(toggle) {
    const panel = this.querySelector('[data-cx-notify-panel]');
    if (!(panel instanceof HTMLElement)) return;
    panel.hidden = false;
    toggle.hidden = true;
    toggle.setAttribute('aria-expanded', 'true');
    const input = panel.querySelector('input[type="email"]');
    if (input instanceof HTMLElement) input.focus();
  }

  /** Lets the "notify me" block show its thank you message after the page reloads. */
  onNotifySubmit() {
    try {
      sessionStorage.setItem(
        'cx-notify-posted',
        JSON.stringify({ productId: this.dataset.productId, variantId: this.notifyVariantId })
      );
    } catch (e) {}
  }

  /** Selects the recommended size in the product's variant picker. @param {string} size */
  selectSize(size) {
    const scope = this.closest('.shopify-section') || document;
    const picker = scope.querySelector('variant-picker');
    if (!picker) return;
    const index = Number(this.dataset.optionPosition || 1);

    const radio = Array.from(picker.querySelectorAll(`input[type="radio"][data-input-id^="${index}-"]`)).find(
      (input) => input instanceof HTMLInputElement && input.value === size
    );
    if (radio instanceof HTMLInputElement) {
      if (!radio.checked) radio.click();
      return;
    }

    const select = picker.querySelectorAll('select')[index - 1];
    if (select instanceof HTMLSelectElement && select.value !== size) {
      select.value = size;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /** @param {number} variantId */
  async addToCart(variantId) {
    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
      .filter(Boolean);

    const formData = new FormData();
    formData.append('id', String(variantId));
    formData.append('quantity', '1');
    if (sectionIds.length) formData.append('sections', sectionIds.join(','));

    const config = fetchConfig('javascript', { body: formData });

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        ...config,
        headers: { ...config.headers, Accept: 'text/html' },
      });
      const data = await response.json();
      if (data.status) return false;

      this.dispatchEvent(
        new CartAddEvent({}, String(variantId), {
          source: 'cx-find-size',
          itemCount: 1,
          productId: this.dataset.productId,
          variantId: String(variantId),
          sections: data.sections,
        })
      );
      return true;
    } catch (error) {
      console.error(error);
      return false;
    }
  }
}

if (!customElements.get('cx-find-size')) {
  customElements.define('cx-find-size', CxFindSize);
}
